import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { AllLabels, FeedInOperationMode, FormatterData, AdditionalMeasuringConcepts } from '../../constants/app.constants';
import { NavigationStateShareService } from '../../services/navigation-state-share.service';
import { DateFormat, EntityType, FormTypesData, KeyValueData } from '../../domains/common.domain';
import { CreateFeedInInstallationResponse, FeedInInstallationDataObject, LookupOptionsData } from '../jurisdiction.domain';
import { AllDropdownKeys, ComponentRoutes, UrlSegments } from '../jurisdiction.constants';
import { CommonService } from '../../services/common.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { CommunicationService } from '../../services/communication.service';
import { ErrorService } from '../../services/error/error.service';
import { TechnicalDataService } from './technical-data.service';
import { GetInstallationApplicationObject, InstallationApplicationData } from '../facilities-form/facilities-form.domain';
import { FacilitiesFormService } from '../facilities-form/facilities-form.service';
import { EnvironmentService } from '../../services/environment.service';
import { NetzTranslateService } from '../../services/netz-translate.service';
import { DestroyableComponent } from '../../common/destroyable.component';
import { ComponentCanDeactivate } from 'src/app/+shared/guards/pending-changes-guard.servie';
import { SectionIdentifier } from 'src/app/+shared/components/review-section/review-section.domain';

@Component({
  providers: [FacilitiesFormService],
  selector: 'app-technical-data',
  templateUrl: './technical-data.component.html',
  styleUrls: ['./technical-data.component.scss'],
})
export class TechnicalDataComponent extends DestroyableComponent implements OnInit, ComponentCanDeactivate {
  @Input() isReadOnly: boolean = false;

  allUnits: KeyValueData;
  technicalDataFG: FormGroup;
  attachments: FormArray;
  inverters: FormArray;
  isLoading: boolean;
  generators: FormArray;
  storages: FormArray;
  unitLabels: KeyValueData;
  facilityTypes: FormTypesData[];
  feedInSystemTypeKey: string;
  feedInTypeKey: string;
  feedInSystems: LookupOptionsData[];
  measuringConceptKey: string;
  measuringConcepts: LookupOptionsData[];
  feedInInstallation: FeedInInstallationDataObject;
  feedInInstallationType: string;
  infoBubblesData: {
    [key: string]: boolean;
  };
  installationId: string;
  dateFormat: DateFormat;
  operationMode: {
    [key: string]: string;
  };
  externalApplicationId: string;
  showManufacturerAndType: boolean = this.environmentService.showManufacturerAndType;
  hideOtherFuelsSystemType: boolean = this.environmentService.hideOtherFuelsSystemType;
  showAdditionalMeasuringConcepts: boolean = this.environmentService.showAdditionalMeasuringConcepts;
  setCommissioningDateNoneMandatory: boolean = this.environmentService.setCommissioningDateNoneMandatory;
  floatNumberFeedinSystemMaxPower: number = this.environmentService.floatNumberFeedinSystemMaxPower;
  switchFeedInSystemExcessEnergyRadioButtons: boolean = this.environmentService.switchFeedInSystemExcessEnergyRadioButtons;
  generatorPowerInWatts: boolean = this.environmentService.generatorPowerInWatts;
  hideFeedInOperationModeSubRadioButtons: boolean = this.environmentService.hideFeedInOperationModeSubRadioButtons;
  showStorageSubsection: boolean = this.environmentService.showStorageSubsection;
  isDetailsView: boolean;
  isSelectionOfMeasuringConceptRequired: boolean = true;
  preventCanDeactivateCheck: boolean = false;
  sectionIdentifier = SectionIdentifier;
  applicationId: string;

  get hideSystemOperationSection(): boolean {
    const installationType = JSON.parse(localStorage.getItem('installation_flow'));
    return this.environmentService.hideSystemOperationSection && installationType === 'feedInSystem';
  }

  get hideInstallationSection(): boolean {
    const installationType = JSON.parse(localStorage.getItem('installation_flow'));
    return this.environmentService.hideInstallationSection && installationType === 'feedInSystem';
  }

  constructor(
    private route: Router,
    private stateShare: NavigationStateShareService,
    private commonService: CommonService,
    private localStorageService: LocalStorageService,
    private communicationService: CommunicationService,
    private environmentService: EnvironmentService,
    private errorService: ErrorService,
    private technicalDataService: TechnicalDataService,
    private facilitiesFormService: FacilitiesFormService,
    private translate: NetzTranslateService,
    private activatedRoute: ActivatedRoute
  ) {
    super();
    this.attachments = new FormArray([]);
    this.inverters = new FormArray([]);
    this.generators = new FormArray([]);
    this.storages = new FormArray([]);
    this.unitLabels = AllLabels.units;
  }

  canDeactivate(): boolean | Promise<boolean> {
    if (this.preventCanDeactivateCheck) {
      return true;
    }

    return !this.technicalDataFG?.dirty;
  }

  ngOnInit(): void {
    this.applicationId = this.localStorageService.getItem('application_id');
    this.preventLeak(this.activatedRoute.url).subscribe(urlSegments => {
      this.isDetailsView = urlSegments[0].path === ComponentRoutes.ProcessDetails;
    });

    this.allUnits = AllLabels.units;
    this.feedInSystemTypeKey = AllDropdownKeys.FeedInSystemType;
    this.feedInTypeKey = AllDropdownKeys.FeedInType;
    this.feedInSystems = this.commonService.getDropdownData(this.feedInSystemTypeKey);
    this.LoadMeasuringConceptsOptions();

    this.facilityTypes = this.localStorageService.getItem('selected_facilities');
    this.feedInInstallationType = this.localStorageService.getItem('feedin_inst_type');
    this.installationId = this.localStorageService.getItem('installation_id');
    this.operationMode = FeedInOperationMode;
    this.dateFormat = FormatterData.date;
    this.isLoading = false;
    this.infoBubblesData = this.environmentService.getInfoBubblesData('technicalData') || null;

    if (this.isReadOnly) {
      this.parseData();
    } else {
      this.getApplicationData();
    }

    this.registerForLanguageChange();
  }

  getFacilitySystemPowerLabel(facilityType: string): string {
    if (this.environmentService.showMaxPowerLabelAccordingToSubproduct && this.feedInInstallationType === 'extension') {
      return 'technical-data.total-output-new-existing-plan';
    }

    if (facilityType === 'storage') {
      return 'technical-data.storage-system-power';
    }

    return 'technical-data.max-power';
  }

  setTechnicalFormValues() {
    this.technicalDataFG.patchValue({
      alreadyExists: !!+this.feedInInstallation.generator_device_exists,
      systemPlanned: !!+this.feedInInstallation.storage_device_planned,
      feedInOperationMode: this.feedInInstallation.sk_feedin_operation_mode || FeedInOperationMode.islandMode,
      finalConsumers: !!+this.feedInInstallation.netconsumer_supply,
      altConcept: !!+this.feedInInstallation.alternative_measuring_concept,
      compensation_date: this.feedInInstallation.compensation_date
        ? this.commonService.parseDateForUI(this.feedInInstallation.compensation_date)
        : '',
      concept: this.measuringConcepts.filter(concept => concept.value === this.feedInInstallation.sk_measuring_concept)[0],
    });

    if (
      this.feedInInstallation.sk_feedin_operation_mode === this.operationMode.feedInExcess &&
      !this.hideFeedInOperationModeSubRadioButtons
    ) {
      this.technicalDataFG.addControl(
        'feedInExcess',
        new FormControl({
          value: this.feedInInstallation.sk_feedin_excess_percentage || '70',
          disabled: this.isReadOnly,
        })
      );
    } else if (
      this.feedInInstallation.sk_feedin_operation_mode === this.operationMode.feedInFull &&
      !this.hideFeedInOperationModeSubRadioButtons
    ) {
      this.technicalDataFG.addControl(
        'feedInFull',
        new FormControl({
          value: this.feedInInstallation.sk_feedin_full_percentage || '60',
          disabled: this.isReadOnly,
        })
      );
      this.technicalDataFG.addControl('otherFeedInValue', new FormControl(this.feedInInstallation.feedin_percentage_other || '0'));
    }
    if (this.feedInInstallation.facility && this.feedInInstallation.facility.length) {
      (this.technicalDataFG.get(EntityType.Attachment) as FormArray).removeAt(0);
      this.feedInInstallation.facility.map(facility => {
        (this.technicalDataFG.get(EntityType.Attachment) as FormArray).push(
          new FormGroup({
            systemType: new FormControl(
              this.feedInSystems.filter(system => {
                if (system.value === facility.sk_feedin_type) {
                  return system;
                }
              })[0],
              Validators.required
            ),
            systemMaxPower: new FormControl(facility.max_apparent_power, Validators.required),
            facilityId: new FormControl(facility.facility_id),
          })
        );
      });
    }
    if (this.feedInInstallation.inverter && this.feedInInstallation.inverter.length) {
      (this.technicalDataFG.get(EntityType.Inverter) as FormArray).removeAt(0);
      this.feedInInstallation.inverter.map(inverter => {
        (this.technicalDataFG.get(EntityType.Inverter) as FormArray).push(
          new FormGroup({
            ...(this.showManufacturerAndType && {
              manufacturer: new FormControl(inverter.manufacturer, [Validators.required]),
            }),
            ...(this.showManufacturerAndType && {
              type: new FormControl(inverter.inverter_type, [Validators.required]),
            }),
            no_of_units: new FormControl(inverter.num_identical_units, [Validators.required]),
            max_power: new FormControl(inverter.max_apparent_power),
            inverterId: new FormControl(inverter.inverter_id),
          })
        );
      });
    }
    if (this.feedInInstallation.generator && this.feedInInstallation.generator.length) {
      (this.technicalDataFG.get(EntityType.Generator) as FormArray).removeAt(0);
      this.feedInInstallation.generator.map(generator => {
        (this.technicalDataFG.get(EntityType.Generator) as FormArray).push(
          new FormGroup({
            ...(this.showManufacturerAndType && {
              manufacturer: new FormControl(generator.manufacturer, [Validators.required]),
            }),
            ...(this.showManufacturerAndType && {
              type: new FormControl(generator.generator_type, [Validators.required]),
            }),
            no_of_units: new FormControl(generator.num_identical_units, [Validators.required]),
            max_power: new FormControl(generator.max_apparent_power),
            generatorId: new FormControl(generator.generator_id),
          })
        );
      });
    }
    if (this.feedInInstallation.storage?.length) {
      (this.technicalDataFG.get(EntityType.Storage) as FormArray).removeAt(0);
      this.feedInInstallation.storage.map(storage => {
        (this.technicalDataFG.get(EntityType.Storage) as FormArray).push(
          new FormGroup({
            ...(this.showManufacturerAndType && {
              manufacturer: new FormControl(storage.manufacturer, [Validators.required]),
            }),
            ...(this.showManufacturerAndType && {
              type: new FormControl(storage.storage_type, [Validators.required]),
            }),
            no_of_units: new FormControl(storage.num_identical_units, [Validators.required]),
            max_power: new FormControl(storage.max_apparent_power),
            storage_id: new FormControl(storage.storage_id),
          })
        );
      });
    }
  }

  addEntity(type: string): void {
    if (type === EntityType.Attachment) {
      (this.technicalDataFG.get(EntityType.Attachment) as FormArray).push(
        new FormGroup({
          systemType: new FormControl('', Validators.required),
          systemMaxPower: new FormControl('', Validators.required),
          facilityId: new FormControl(''),
        })
      );
      this.attachments = this.technicalDataFG.get(EntityType.Attachment) as FormArray;
    } else if (type === EntityType.Inverter) {
      (this.technicalDataFG.get(EntityType.Inverter) as FormArray).push(
        new FormGroup({
          ...(this.showManufacturerAndType && {
            manufacturer: new FormControl('', [Validators.required]),
          }),
          ...(this.showManufacturerAndType && {
            type: new FormControl('', [Validators.required]),
          }),
          no_of_units: new FormControl('', [Validators.required]),
          max_power: new FormControl(''),
          inverterId: new FormControl(null),
        })
      );
      this.inverters = this.technicalDataFG.get(EntityType.Inverter) as FormArray;
    } else if (type === EntityType.Generator) {
      (this.technicalDataFG.get(EntityType.Generator) as FormArray).push(
        new FormGroup({
          ...(this.showManufacturerAndType && {
            manufacturer: new FormControl('', [Validators.required]),
          }),
          ...(this.showManufacturerAndType && {
            type: new FormControl('', [Validators.required]),
          }),
          no_of_units: new FormControl('', [Validators.required]),
          max_power: new FormControl(''),
          generatorId: new FormControl(null),
        })
      );
      this.generators = this.technicalDataFG.get(EntityType.Generator) as FormArray;
    } else if (type === EntityType.Storage) {
      (this.technicalDataFG.get(EntityType.Storage) as FormArray).push(
        new FormGroup({
          ...(this.showManufacturerAndType && {
            manufacturer: new FormControl('', [Validators.required]),
          }),
          ...(this.showManufacturerAndType && {
            type: new FormControl('', [Validators.required]),
          }),
          no_of_units: new FormControl('', [Validators.required]),
          max_power: new FormControl(''),
          storageId: new FormControl(null),
        })
      );
      this.storages = this.technicalDataFG.get(EntityType.Storage) as FormArray;
    }
  }

  saveFormData(): void {
    this.saveFeedInInstallation();
    if (!this.isDetailsView) {
      this.preventCanDeactivateCheck = true;
      this.route.navigate(['/personal-data']);
      this.stateShare.setFormState(3);
    }
  }

  checkForOperationModes(): void {
    if (
      this.technicalDataFG.controls.feedInOperationMode.value === FeedInOperationMode.feedInExcess &&
      !this.hideFeedInOperationModeSubRadioButtons
    ) {
      this.technicalDataFG.addControl(
        'feedInExcess',
        new FormControl({
          value: this.feedInInstallation !== null ? this.feedInInstallation.sk_feedin_excess_percentage || '70' : '70',
          disabled: this.isReadOnly,
        })
      );
      this.technicalDataFG.removeControl('feedInFull');
      this.technicalDataFG.removeControl('otherFeedInValue');
    } else if (
      this.technicalDataFG.controls.feedInOperationMode.value === FeedInOperationMode.feedInFull &&
      !this.hideFeedInOperationModeSubRadioButtons
    ) {
      this.technicalDataFG.addControl(
        'feedInFull',
        new FormControl({
          value: this.feedInInstallation.sk_feedin_full_percentage || '60',
          disabled: this.isReadOnly,
        })
      );
      this.technicalDataFG.addControl('otherFeedInValue', new FormControl(this.feedInInstallation.feedin_percentage_other || '0'));
      this.technicalDataFG.removeControl('feedInExcess');
    } else {
      this.technicalDataFG.removeControl('feedInFull');
      this.technicalDataFG.removeControl('feedInExcess');
      this.technicalDataFG.removeControl('otherFeedInValue');
    }
  }

  removeEntity(type: string, index: number): void {
    if (type === EntityType.Attachment) {
      const facilityId =
        (this.feedInInstallation.facility &&
          this.feedInInstallation.facility[index] &&
          this.feedInInstallation.facility[index].facility_id) ||
        '';
      if (facilityId) {
        this.communicationService.deleteData(`${UrlSegments.FeedInFacility}?facility_id=${facilityId}`).subscribe(
          response => {
            (this.technicalDataFG.get(EntityType.Attachment) as FormArray).removeAt(index);
            this.feedInInstallation.facility.splice(index, 1);
            this.errorService.throwErrorIfFailure(response, true);
          },
          error => {
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      } else {
        (this.technicalDataFG.get(EntityType.Attachment) as FormArray).removeAt(index);
      }
    } else if (type === EntityType.Inverter) {
      const inverterId =
        (this.feedInInstallation.inverter &&
          this.feedInInstallation.inverter[index] &&
          this.feedInInstallation.inverter[index].inverter_id) ||
        '';
      if (inverterId) {
        this.communicationService.deleteData(`${UrlSegments.FeedInInverter}?inverter_id=${inverterId}`).subscribe(
          response => {
            (this.technicalDataFG.get(EntityType.Inverter) as FormArray).removeAt(index);
            this.feedInInstallation.inverter.splice(index, 1);
            this.errorService.throwErrorIfFailure(response, true);
          },
          error => {
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      } else {
        (this.technicalDataFG.get(EntityType.Inverter) as FormArray).removeAt(index);
      }
    } else if (type === EntityType.Generator) {
      const generatorId =
        (this.feedInInstallation.generator &&
          this.feedInInstallation.generator[index] &&
          this.feedInInstallation.generator[index].generator_id) ||
        '';
      if (generatorId) {
        this.communicationService.deleteData(`${UrlSegments.FeedInGenerator}?generator_id=${generatorId}`).subscribe(
          response => {
            (this.technicalDataFG.get(EntityType.Generator) as FormArray).removeAt(index);
            this.feedInInstallation.generator.splice(index, 1);
            this.errorService.throwErrorIfFailure(response, true);
          },
          error => {
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      } else {
        (this.technicalDataFG.get(EntityType.Generator) as FormArray).removeAt(index);
      }
    } else if (type === EntityType.Storage) {
      const storageId =
        (this.feedInInstallation.storage && this.feedInInstallation.storage[index] && this.feedInInstallation.storage[index].storage_id) ||
        '';
      if (storageId) {
        this.communicationService.deleteData(`${UrlSegments.feedinstorage}?storage_id=${storageId}`).subscribe(
          response => {
            (this.technicalDataFG.get(EntityType.Storage) as FormArray).removeAt(index);
            this.feedInInstallation.storage.splice(index, 1);
            this.errorService.throwErrorIfFailure(response, true);
          },
          error => {
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      } else {
        (this.technicalDataFG.get(EntityType.Storage) as FormArray).removeAt(index);
      }
    }
  }

  updateSelectionOfMeasuringConceptObligation(): void {
    if (this.environmentService.shouldSelectionOfMeasuringConceptBeNoneMandatory) {
      if (this.technicalDataFG.controls.altConcept.value) {
        this.technicalDataFG.controls.concept.clearValidators();
        this.isSelectionOfMeasuringConceptRequired = false;
      } else {
        this.technicalDataFG.controls.concept.setValidators(Validators.required);
        this.isSelectionOfMeasuringConceptRequired = true;
      }
      this.technicalDataFG.controls.concept.updateValueAndValidity();
    }
  }

  private getApplicationData(): void {
    this.isLoading = true;

    this.facilitiesFormService.getInstallationApplication().subscribe(
      (response: GetInstallationApplicationObject) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        this.commonService.installationApplicationData = response.installationapplication[0];
        this.parseData();
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private prepareForm(): void {
    this.technicalDataFG = new FormGroup({
      attachments: new FormArray([
        new FormGroup({
          systemType: new FormControl('', Validators.required),
          systemMaxPower: new FormControl('', Validators.required),
        }),
      ]),
      inverters: new FormArray([
        new FormGroup({
          ...(this.showManufacturerAndType && {
            manufacturer: new FormControl('', [Validators.required]),
          }),
          ...(this.showManufacturerAndType && {
            type: new FormControl('', [Validators.required]),
          }),
          no_of_units: new FormControl('', [Validators.required]),
          max_power: new FormControl(''),
        }),
      ]),
      generators: new FormArray([
        new FormGroup({
          ...(this.showManufacturerAndType && {
            manufacturer: new FormControl('', [Validators.required]),
          }),
          ...(this.showManufacturerAndType && {
            type: new FormControl('', [Validators.required]),
          }),
          no_of_units: new FormControl('', [Validators.required]),
          max_power: new FormControl(''),
        }),
      ]),
      alreadyExists: new FormControl(false, [Validators.required]),
      systemPlanned: new FormControl(false, [Validators.required]),
      finalConsumers: new FormControl(false, [Validators.required]),
      feedInOperationMode: new FormControl(
        {
          value: FeedInOperationMode.islandMode,
          disabled: this.isReadOnly,
        },
        [Validators.required]
      ),
      concept: new FormControl('', [Validators.required]),
      ...(!this.environmentService.hideAlternativeConceptCheckBox && {
        altConcept: new FormControl('', [Validators.required]),
      }),
      price: new FormControl(''),
      compensation_date: new FormControl(
        {
          value: null,
          disabled: this.isReadOnly,
        },
        this.hideInstallationSection || this.setCommissioningDateNoneMandatory ? [] : [Validators.required]
      ),
    });

    if (this.showStorageSubsection) {
      this.technicalDataFG.addControl(
        'storages',
        new FormArray([
          new FormGroup({
            ...(this.showManufacturerAndType && {
              manufacturer: new FormControl('', [Validators.required]),
            }),
            ...(this.showManufacturerAndType && {
              type: new FormControl('', [Validators.required]),
            }),
            no_of_units: new FormControl('', [Validators.required]),
            max_power: new FormControl(''),
          }),
        ])
      );

      this.storages = this.technicalDataFG.get(EntityType.Storage) as FormArray;
    }

    this.attachments = this.technicalDataFG.get(EntityType.Attachment) as FormArray;
    this.inverters = this.technicalDataFG.get(EntityType.Inverter) as FormArray;
    this.generators = this.technicalDataFG.get(EntityType.Generator) as FormArray;

    if (this.feedInInstallation) {
      this.setTechnicalFormValues();
    }
    this.updateSelectionOfMeasuringConceptObligation();
  }

  private parseData() {
    const installationData: InstallationApplicationData = { ...this.commonService.installationApplicationData };
    this.externalApplicationId = this.commonService.installationApplicationId;
    this.feedInInstallation = installationData.feedininstallation;
    if (!this.feedInInstallationType) {
      this.feedInInstallationType = this.feedInInstallation.sk_feedin_inst_type;
    }
    if (this.isReadOnly) {
      this.installationId = this.feedInInstallation.installation_id;
    }
    this.prepareForm();
  }

  private saveFeedInInstallation(): void {
    const feedInInstallationTechnicalData = this.technicalDataService.getFormValues(this.technicalDataFG);
    const requestData = this.technicalDataService.prepareFeedInInstallationRequestData(feedInInstallationTechnicalData);
    if (this.installationId) {
      delete requestData.application_id;
      const updateUrl = UrlSegments.FeedInInstallation + '?installation_id=' + this.installationId;
      this.communicationService.updateData(`${updateUrl}`, requestData).subscribe(
        response => {
          this.errorService.throwErrorIfFailure(response, true);
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
      this.saveFeedInFacility(this.installationId);
      this.saveFeedInInverter(this.installationId);
      this.saveFeedInGenerator(this.installationId);
      this.saveFeedInStorage(this.installationId);
    } else {
      const postUrl = UrlSegments.FeedInInstallation;
      this.communicationService.postData(`${postUrl}`, requestData).subscribe(
        (response: CreateFeedInInstallationResponse) => {
          this.localStorageService.setItem('installation_id', response.feedininstallation[0].installation_id);
          this.saveFeedInFacility(response.feedininstallation[0].installation_id);
          this.saveFeedInInverter(response.feedininstallation[0].installation_id);
          this.saveFeedInGenerator(response.feedininstallation[0].installation_id);
          this.saveFeedInStorage(response.feedininstallation[0].installation_id);
          this.errorService.throwErrorIfFailure(response, true);
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    }
  }

  private saveFeedInFacility(installationId: string): void {
    (this.technicalDataFG.get(EntityType.Attachment) as FormArray).controls.forEach(attachment => {
      const requestData = this.technicalDataService.prepareFeedInFacilityRequestData(installationId, attachment);
      if (attachment.value.facilityId) {
        const updateUrl = UrlSegments.FeedInFacility + '?facility_id=' + attachment.get('facilityId').value;
        delete requestData.installation_id;
        this.communicationService.updateData(`${updateUrl}`, requestData).subscribe(
          (response: any) => {
            this.errorService.throwErrorIfFailure(response, true);
          },
          error => {
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      } else {
        const postUrl = UrlSegments.FeedInFacility;
        this.communicationService.postData(`${postUrl}`, requestData).subscribe(
          (response: any) => {
            this.errorService.throwErrorIfFailure(response, true);
          },
          error => {
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      }
    });
  }

  private saveFeedInInverter(installationId: string): void {
    (this.technicalDataFG.get(EntityType.Inverter) as FormArray).controls.forEach(inverter => {
      const requestData = this.technicalDataService.prepareFeedInInverterRequestData(installationId, inverter);
      if (inverter.value.inverterId) {
        const updateUrl = UrlSegments.FeedInInverter + '?inverter_id=' + inverter.get('inverterId').value;
        delete requestData.installation_id;
        this.communicationService.updateData(`${updateUrl}`, requestData).subscribe(
          (response: any) => {
            this.errorService.throwErrorIfFailure(response, true);
          },
          error => {
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      } else {
        const postUrl = UrlSegments.FeedInInverter;
        this.communicationService.postData(`${postUrl}`, requestData).subscribe(
          (response: any) => {
            this.errorService.throwErrorIfFailure(response, true);
          },
          error => {
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      }
    });
  }

  private saveFeedInGenerator(installationId: string): void {
    (this.technicalDataFG.get(EntityType.Generator) as FormArray).controls.forEach(generator => {
      const requestData = this.technicalDataService.prepareFeedInGeneratorRequestData(installationId, generator);
      if (generator.value.generatorId) {
        const updateUrl = UrlSegments.FeedInGenerator + '?generator_id=' + generator.get('generatorId').value;
        delete requestData.installation_id;
        this.communicationService.updateData(`${updateUrl}`, requestData).subscribe(
          (response: any) => {
            this.errorService.throwErrorIfFailure(response, true);
          },
          error => {
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      } else {
        const postUrl = UrlSegments.FeedInGenerator;
        this.communicationService.postData(`${postUrl}`, requestData).subscribe(
          (response: any) => {
            this.errorService.throwErrorIfFailure(response, true);
          },
          error => {
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      }
    });
  }

  private saveFeedInStorage(installationId: string): void {
    (this.technicalDataFG.get(EntityType.Storage) as FormArray)?.controls.forEach(storage => {
      const requestData = this.technicalDataService.prepareFeedInStorageRequestData(installationId, storage);
      if (storage.value.storageId) {
        const updateUrl = UrlSegments.FeedInStorage + '?storage_id=' + storage.get('storageId').value;
        delete requestData.installation_id;
        this.communicationService.updateData(`${updateUrl}`, requestData).subscribe(
          (response: any) => {
            this.errorService.throwErrorIfFailure(response, true);
          },
          error => {
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      } else {
        const postUrl = UrlSegments.FeedInStorage;
        this.communicationService.postData(`${postUrl}`, requestData).subscribe(
          (response: any) => {
            this.errorService.throwErrorIfFailure(response, true);
          },
          error => {
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      }
    });
  }

  private registerForLanguageChange(): void {
    this.preventLeak(this.translate.registerForLanguageChange()).subscribe(() => {
      this.initDropdownCollections();
    });
  }

  private initDropdownCollections(): void {
    this.feedInSystems = this.commonService.getDropdownData(this.feedInSystemTypeKey);
    if (this.hideOtherFuelsSystemType) {
      const index = this.feedInSystems.findIndex(system => system.value === 'other_fuel');
      this.feedInSystems.splice(index, 1);
    }
  }

  private LoadMeasuringConceptsOptions() {
    this.measuringConceptKey = AllDropdownKeys.FeedInMeasuringConcept;
    this.measuringConcepts = this.commonService.getDropdownData(this.measuringConceptKey);

    if (this.showAdditionalMeasuringConcepts) {
      // We need to remove old values used in other clients/products
      this.measuringConcepts = [];
      this.addAdditionalMeasuringConcepts();
    }
  }

  private addAdditionalMeasuringConcepts(): void {
    AdditionalMeasuringConcepts.forEach(concept => {
      this.measuringConcepts.push({
        label: concept,
        order: this.measuringConcepts.length + 1,
        value: concept.replace(/\s+/g, ''),
      });
    });
  }
}
